import { cloneDeep, isEqual, uniq, uniqBy } from 'lodash'
import isMobile from 'piramis-js-utils/lib/isMobile'
import Rete, { Input } from 'rete'
import { Connection } from 'rete/types/connection'
import { EventsTypes } from 'rete/types/events'
import { Selected } from 'rete/types/selected'
import { ConnectionView } from 'rete/types/view/connection'
import Vue from 'vue'
import i18n from 'vue-i18n'

import CustomInput from '../../components/Socket/CustomInput'
import CustomOutput from '../../components/Socket/CustomOutput'
import { SELF_NODE_ITEM, SocketNames } from '../../components/Socket/SocketsHelper'
import { getPickerColor } from '../../utils/getPickerColor'
import { CustomConnection } from '../Connections/CustomConnection'
import { FlowStatistics } from '../FlowStatistics'
import FlowHelper from '../Helpers/helper'
import { BaseNodeStruct } from '../Nodes'
import CustomNode from '../Nodes/CustomNode'
import { Events } from '../types/EditorTypes/Events'
import { StartNode } from './StartNode/StartNode'
import { EditorViewMode } from './types'
import { CopyVisitor } from './utils/CopyVisitor'

type PseudoConnectionState = {
  pick: boolean,
  connectionCreated: boolean
}

type AreaState = {
  clickable: boolean,
  movable: boolean
}

export default class CustomEditorEvents extends Rete.NodeEditor {
  nodes: Array<CustomNode> = []

  sockets: Record<string, CustomInput | CustomOutput> = {}

  sidebarIsOpen = false

  private pseudoConnectionState: PseudoConnectionState = {
    pick: false,
    connectionCreated: false,
  }

  private areaState: AreaState = {
    clickable: true,
    movable: true
  }

  private selectedConnections: Array<CustomConnection['data']['id']> | null = null

  private clipNodes: Array<number> = []

  protected nodesPlaints: Array<BaseNodeStruct> = []

  protected i18n: i18n

  declare selected: Omit<Selected, 'list'> & { list: Array<CustomNode> }

  protected eventTarget: EventTarget = new EventTarget()

  statistics: FlowStatistics | null = null

  private _viewMode: EditorViewMode = EditorViewMode.Editor

  get viewMode(): EditorViewMode {
    return this._viewMode
  }

  set viewMode(value: EditorViewMode) {
    this._viewMode = value

    if (this.statistics) {
      if (value === EditorViewMode.Statistics) {
        this.statistics.getConnectionsStatistics()
      } else {
        this.statistics.resetState()
      }

      this.toggleBackground()
    }
  }

  get hasClipNodes() {
    return this.clipNodes.length > 0
  }

  toggleBackground() {
    const background = document.querySelector<HTMLDivElement>(".rete-background")

    if (background) {
      if (background.style.display === 'table' || !background.style.display.length) {
        background.style.display = 'none'
      } else {
        background.style.display = 'table'
      }
    }
  }

  get isStatisticsViewSelected() {
    return this.viewMode === EditorViewMode.Statistics
  }

  trigger<K extends keyof Events>(name: K, params?: Events[K]): boolean {
    return super.trigger(name as keyof EventsTypes, params)
  }

  on<K extends keyof Events>(names: K | K[], handler: (args: Events[K]) => void | unknown): this {
    return super.on(names as keyof EventsTypes, handler as any)
  }

  constructor(container: HTMLElement, i18n: i18n) {
    super('piramis@0.1.0', container)

    this.i18n = i18n
    this.bind('triggerContextMenu')
  }

  private pick = {
    time: 0,
    x: 0,
    y: 0,
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getSocketBuPoints([ outX, outY ]: [number, number], [ inpX, inpY ]: [number, number]): CustomOutput | CustomInput | null {
    const iter = this.view.nodes.values()

    let i = 0
    let socket: CustomOutput | CustomInput | null = null

    while (i < this.nodes.length && !socket) {
      const nodeView = iter.next().value
      const node = this.nodes[i]

      if (node.mainInput) {
        const input = node.inputs.get(node.mainInput) as CustomInput

        if (input && isEqual(nodeView.getSocketPosition(input), [ inpX, inpY ])) {
          socket = input
        }
      }

      const outputsIter = node.outputs.values()

      let j = 0

      while (j < node.outputs.size && !socket) {
        const io = outputsIter.next().value

        if (io.isVisible && io.key !== '-1') {
          const socketPoints = nodeView.getSocketPosition(io)

          if (isEqual(socketPoints, [ outX, outY ])) {
            socket = io
          }
        }

        j++
      }
      i++
    }

    return socket
  }

  private findNode(x: number, y: number): CustomNode | undefined {
    const iter = this.view.nodes.values()

    let node: CustomNode | undefined = undefined
    let i = 0

    while (i < this.view.nodes.size && !node) {
      const nodeView = iter.next().value

      const [ nodeX, nodeY ] = nodeView.node.position
      const nodeH = nodeView.el.clientHeight + nodeY
      const nodeW = nodeView.el.clientWidth + nodeX

      const xCheck = x > nodeX && nodeW > x
      const yCheck = y > nodeY && nodeH > y

      if (xCheck && yCheck) {
        node = this.nodes[i]
      }

      i++
    }

    return node
  }

  private togglePseudoConnectionFields(record: Partial<Record<keyof PseudoConnectionState, boolean>>) {
    if (record.pick !== undefined) {
      this.pseudoConnectionState.pick = record.pick
    }

    if (record.connectionCreated !== undefined) {
      this.pseudoConnectionState.connectionCreated  = record.connectionCreated
    }
  }

  /**
  * could be used in project
  * */
  toggleAreaStateFields(record: Partial<Record<keyof AreaState, boolean>>) {
    if (record.clickable !== undefined) {
      this.areaState.clickable = record.clickable
    }

    if (record.movable !== undefined) {
      this.areaState.movable  = record.movable
    }
  }

  pseudoOutput: CustomOutput | null = null

  // eslint-disable-next-line sonarjs/cognitive-complexity
  mutationObserver: MutationObserver = new MutationObserver(([ pseudo ]) => {
    if (pseudo.addedNodes.length) {
      const svg = pseudo.addedNodes[0] as SVGElement

      const path = svg.childNodes[0] as SVGPathElement

      const [ , outX, outY, , , , , , inpX, inpY ] = path.attributes.getNamedItem('d')!.value.split(' ')

      const initSocket = this.getSocketBuPoints([ Number(outX), Number(outY) ], [ Number(inpX), Number(inpY) ])

      if (initSocket) {
        path.style.stroke = `rgb(${ FlowHelper.typeSocketColor(initSocket.name as SocketNames) })`
      }
    }

    if (pseudo.removedNodes.length) {
      const svg = pseudo.removedNodes[0] as SVGElement

      const path = svg.childNodes[0] as SVGPathElement

      const [ M, outX, outY, C, , , , , inpX, inpY ] = path.attributes.getNamedItem('d')!.value.split(' ')

      const initSocket = this.getSocketBuPoints([ Number(outX), Number(outY) ], [ Number(inpX), Number(inpY) ])

      if (initSocket && initSocket instanceof CustomOutput) {
        const node = this.findNode(Number(inpX), Number(inpY))

        if (node && node.mainInput) {
          this.connect(initSocket, node.inputs.get(node.mainInput)!, {
            visible: true,
            color: null,
            d: '',
            id: CustomConnection.LastConnectionId += 1,
            statistics: null
          })
        } else {
          if (this.pseudoConnectionState.pick && !this.pseudoConnectionState.connectionCreated) {
            requestAnimationFrame(() => this.trigger('triggerContextMenu', { show: true }))

            this.togglePseudoConnectionFields({ connectionCreated: false, pick: false })
          }
        }
      }
    }
  })

  setEvents(): void {
    this.mutationObserver.observe(this.view.area.el.firstChild as HTMLElement, { childList: true })

    this
      .on('translate', () => {
        if (!this.areaState.movable) {
          return false
        }

        this.unselectNodes()
      })
      .on('zoom', (e) => {
        if (!this.areaState.movable) {
          return false
        }

        this.unselectNodes()

        return e.source !== 'dblclick'
      })
      .on('connectionpick', (socket) => {
        if (!this.areaState.clickable) {
          return false
        }

        this.togglePseudoConnectionFields({ connectionCreated: false, pick: true })

        if (this.isStatisticsViewSelected) {
          return false
        }

        if (socket instanceof CustomOutput) {
          this.pseudoOutput = socket
        }

        this.pickAction(socket as CustomOutput | CustomInput)
      })
      .on('connectionremove', (connection) => {
        this.connectionRemove(connection)
      })
      .on('connectiondrop', (socket) => {
        this.dropAction()
      })
      .on('connectioncreate', ({ output, input }) => {
        if (output.node?.id === input.node?.id) {
          return false
        }
      })
      .on('rendersocket', ({
                             input,
                             output
                           }) => this.renderSocket(input as CustomInput, output as CustomOutput))
      .on('renderconnection', ({ connection, el, points }) => {
        this.renderConnection(connection, el, points)

        this.togglePseudoConnectionFields({ connectionCreated: true })
      })
      .on('updateconnection', ({ connection, el, points }) => {
        this.updateConnection(connection, el, points)
      })
      .on('rendernode', ({ node, el }) => {
        let startPosition: Array<number> = []
        el.onpointerdown = () => startPosition = cloneDeep(node.position)

        el.onpointerup = () => {
          if (!isEqual(startPosition, node.position)) {
            this.unselectNodes()
          }
        }
      })
      .on('nodetranslate', () => {
        if (!this.areaState.movable) {
          return false
        }
      })
      .on('nodeselect', () => {
        if (!this.areaState.clickable) {
          return false
        }
      })
      // eslint-disable-next-line sonarjs/cognitive-complexity
      .on('contextmenu', ({ e, node, view }) => {
        if (!this.areaState.clickable) {
          return false
        }

        e.preventDefault()
        e.stopPropagation()

        if (!(node instanceof StartNode)) {

          if (this.isStatisticsViewSelected) {
            return false
          }

          if (!isMobile()) {
            let nodes: Array<CustomNode> | undefined = undefined

            if (view) {
              this.selected.clear()
            }

            if (node) {
              nodes = node ? uniqBy(([
                node,
                ...this.selected.list
              ]), 'id') : undefined
            }

            this.sidebarIsOpen = false

            this.trigger('triggerContextMenu', { nodes, show: true })
          }
        }
      })
      .on('noderemoved', (node) => this.unselectNodes())
      .on('keydown', (e) => !this.sidebarIsOpen && this.keydownAction(e))
      .on('selectnode', args => {
        if ((args.e.target as Element).id === SELF_NODE_ITEM) {
          this.selected.clear()
        } else {
          if (this.viewMode === EditorViewMode.Editor && this.areaState.clickable && !(args.node instanceof StartNode)) {
            this.sidebarIsOpen = true
          }
        }

        this.hideContextMenu()
      })
      .on('translatenode', args => {
        if (!isMobile() || (isMobile() && args.dx !== 0 || args.dy !== 0)) {
          this.sidebarIsOpen = false
        }

        this.hideContextMenu()
      })
      .on('noderemoved', (node) => {
        this.unselectNodes()
      })
  }

  hideContextMenu(): void {
    this.trigger('triggerContextMenu', { show: false })
  }

  unselectNodes(): void {
    this.selected.clear()
    this.hideContextMenu()
  }

  private setConnectionColor(connection: CustomConnection, path: SVGPathElement) {
    if (connection.data.color) {
      const color = getPickerColor(connection.data.color)

      if (color) {
        path.style.stroke = color
      } else {
        path.style.stroke = `rgb(${ FlowHelper.typeSocketColor(connection.output.name) })`
      }
    } else {
      path.style.stroke = `rgb(${ FlowHelper.typeSocketColor(connection.output.name) })`
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private renderConnection(connection: CustomConnection, el: HTMLElement, points: Array<number>): void {
    const svg = el.firstChild as SVGElement
    const path = svg?.firstChild as SVGPathElement
    const d = path.attributes.getNamedItem('d')

    if (d) {
      connection.data.d = d.value
    }

    if (connection.data?.visible === undefined) {
      Vue.prototype.$set(connection.data, 'visible', true)
    }

    Vue.prototype.$set(connection.data, 'statistics', null)

    if (!connection.data?.id) {
      connection.data.id = CustomConnection.LastConnectionId += 1
    }

    svg.style.width = Math.max(points[0], points[2]) + 'px'
    svg.style.height = Math.max(points[1], points[3]) + 'px'

    if (path) {
      path.classList.add('cursor-pointer', 'hover:opacity-75')

      path.style.pointerEvents = 'visibleStroke'

      path.onclick = () => {
        if (!this.areaState.clickable) {
          return false
        }

        if (this.statistics && this.isStatisticsViewSelected) {
          this.statistics.selectedConnection = connection
        } else {
          this.trigger('triggerContextMenu', { show: true, connection })
        }
      }

      this.setConnectionColor(connection, path)
    }
  }

  private renderSocket(input?: CustomInput, output?: CustomOutput): void {
    if (output) {
      this.sockets[output.key] = output
    }

    if (input) {
      this.sockets[input.key] = input
    }
  }

  private connectionRemove(conn: Connection): void {
    conn.output.connections = []

    if (this.view.area.el.firstChild!.firstChild) {
      this.trigger('connectionpick', conn.output)
    }
  }

  private keydownAction(e: KeyboardEvent): void {
    if (e.code === 'Delete' && this.selected.list.length > 1) {
      const nodes = this.selected.list.slice()

      for (let i = nodes.length - 1; i >= 0; i--) {
        this.removeNode(nodes[i])
      }
    }

    if (e.code === 'KeyC' && e.ctrlKey) {
      this.copyNodes()
    }

    if (e.code === 'KeyS' && e.ctrlKey) {
      e.preventDefault()
      const saveFlowEvent = new Event('saveFlow')
      this.eventTarget.dispatchEvent(saveFlowEvent)
    }

    if (e.code === 'KeyV' && e.ctrlKey) {
      this.pasteNodes()
    }
  }

  private dropAction(): void {
    const { x, y } = this.view.area.mouse

    const xCheck = x - 15 < this.pick.x && x + 15 > this.pick.x
    const yCheck = y - 15 < this.pick.y && y + 15 > this.pick.y

    if (Number(new Date) - this.pick.time > 200 || (!xCheck || !yCheck)) {
      this.enableNodesSockets()
    }
  }

  private pickAction(socket: CustomInput | CustomOutput): boolean | void {
    if (!socket.multipleConnections && socket.connections.length > 0) {
      return false
    }
    this.enableNodesSockets()

    this.pick = {
      time: Number(new Date),
      x: this.view.area.mouse.x,
      y: this.view.area.mouse.y
    }

    if (event && event.type !== 'pointerup') {
      if (socket instanceof Input) {
        this.disableNodesSockets('inputs', socket.key)
      } else {
        this.disableNodesSockets('outputs', socket.key)
      }
    }
  }

  copyNodes(nodes?: Array<CustomNode> | null): void {
    const isNotStartNode = (node:CustomNode) => !(node instanceof StartNode)
    const mapToNodeId = (node:CustomNode) => node.id

    const nodeIds = (nodes ?? [])
      .filter(isNotStartNode)
      .map(mapToNodeId)

    this.clipNodes = uniq([
      ...this.selected.list.filter(isNotStartNode).map(mapToNodeId),
      ...nodeIds
    ])
  }

  pasteNodes(): void {
    this.nodesPlaints = CopyVisitor.visit(this.nodes, this.clipNodes, this.view.area.mouse)

    const pasteNodesEvent = new Event('pasteNodes')
    this.eventTarget.dispatchEvent(pasteNodesEvent)
  }

  private enableNodesSockets(): void {
    this.nodes.forEach((node) => node.enableSockets())
  }

  private disableNodesSockets(sockets: 'inputs' | 'outputs', current: string): void {
    this.nodes.forEach((node) => node.disableSockets(sockets, current))
  }

  isConnectionHighlighted(connection: CustomConnection) {
    return this.selectedConnections && this.selectedConnections.includes(connection.data.id)
  }

  toggleConnectionHighlight(connection: CustomConnection) {
    const connectionView = this.view.connections.get(connection)

    if (connectionView) {
      const svg = connectionView.el.getElementsByClassName('main-path')[0] as SVGPathElement

      if (this.selectedConnections === null) {
        this.selectedConnections = []
      }

      if (this.selectedConnections.includes(connection.data.id)) {
        this.selectedConnections = this.selectedConnections.filter(id => id !== connection.data.id)

        this.setConnectionColor(connection, svg)
      } else {
        this.selectedConnections.push(connection.data.id)
        svg.style.stroke = 'rgb(255, 203, 0)'
      }
    }
  }

  toggleConnectionVisibilityHandler(connection: CustomConnection): void {
    const connectionView = this.view.connections.get(connection)

    if (connectionView) {
      const svg = connectionView.el.getElementsByClassName('main-path')[0] as SVGPathElement

      if (connection.data.visible) {
        this.isVisibleConnectionHandler(connection, svg)
      } else {
        this.isNotVisibleConnectionHandler(connection, connectionView)
      }
    }
  }

  isVisibleConnectionHandler(connection: CustomConnection, svg: SVGPathElement): void {
    const d = svg.attributes.getNamedItem('d')

    if (d && d.nodeValue) {
      const [ M, x1, y1, C, dx1, dy1, dx2, dy2, x2, y2 ] = d.nodeValue.split(' ')

      d.value = `M ${ x1 } ${ y1 } ${ Number(x1) + 50 } ${ y1 } M ${ Number(x2) - 50 } ${ y2 } ${ x2 } ${ y2 }`

      connection.data.visible = false

      if (this.selectedConnections) {
        this.selectedConnections = this.selectedConnections.filter(id => id !== connection.data.id)
      }

      this.setConnectionColor(connection, svg)
    }
  }

  isNotVisibleConnectionHandler(connection: CustomConnection, connectionView: ConnectionView): void {
    const d = connection.data.d.split(' ')
    const [ M, x1, y1, C, dx1, dy1, dx2, dy2, x2, y2 ] = d

    connection.data.visible = true

    this.trigger('updateconnection', {
      el: connectionView.el,
      connection: connection,
      points: [ Number(x1), Number(y1), Number(x2), Number(y2) ]
    })
  }

  updateConnection(connection: CustomConnection, el: HTMLElement, points: Array<number>): void {
    const data = connection.data
    const svg = el.getElementsByClassName('main-path')[0] as SVGElement
    const d = svg.attributes.getNamedItem('d')

    if (d) {
      if (data.visible) {
        data.d = d.value.slice()

        if (this.isStatisticsViewSelected && (connection.data.statistics?.value ?? 0) <= 0) {
          svg.classList.add('opacity-30')
        } else {
          svg.classList.remove('opacity-30')
        }
      }

      const [ outX, outY, inpX, inpY ] = points

      if (!data.visible) {
        d.value = `M ${ outX } ${ outY } ${ outX + 50 } ${ outY } M ${ inpX - 50 } ${ inpY } ${ inpX } ${ inpY }`
      }
    }
  }
}
